<template>
  <div id="token-view">
    <div class="pt-10" v-if="tokenId == null">
      <div class="text-h2">{{ $t('token.idOutOfBound') }}</div>
    </div>
    <div class="pt-12" v-else-if="isLoading">
      <div class="text-h2">{{ $t('token.loadingDetails') }}...</div>
      <div class="pa-12">
        <v-progress-circular indeterminate class="mx-auto" size="60" width="5" />
      </div>
    </div>
    <v-row class="h-100 pa-12" justify="center" align="center" v-else-if="isWalletConnected && !isChainMatched">
      <v-btn
        color="accent"
        x-large
        @click="$store.dispatch('web3SwitchChain')"
      >{{ $t('shared.switchToEth') }}</v-btn>
    </v-row>
    <v-row class="pt-10" justify="center" v-else>
      <v-col cols="24" sm="16" md="10" lg="11" class="pa-4">
        <v-img
          :src="imageUri || ''"
          aspect-ratio="1"
          :content-class="imageUri ? '' : 'd-flex justify-center align-center secondary'"
          :alt="tokenTitleText"
          class="mx-auto"
        >
          <v-icon x-large color="black" v-if="!imageUri">mdi-image-off</v-icon>
        </v-img>
      </v-col>
      <v-spacer class="d-none d-md-block" />
      <v-col cols="24" sm="16" md="12" lg="11" class="pa-3">
        <h1 class="primary--text text-start py-6 ">
          {{ tokenTitleText }}
          <a :href="explorerTokenUri(contractAddr, tokenId)" target="_blank" class="text-decoration-none">
            <v-icon small class="ml-2">mdi-open-in-new</v-icon>
          </a>
        </h1>
        <div class="font-weight-medium text-start py-6" v-if="tokenOwnerDisplay">
          {{ $t('token.owner') }}:
          <span class="primary--text">
            <a :href="explorerAddressUri(tokenOwner)" target="_blank" class="text-decoration-none">
              {{ contractAddr  | shortenHash }}
              <v-icon small class="ml-2">mdi-open-in-new</v-icon>
            </a>
            <v-icon @click="$root.$emit('copy-text', tokenOwner)" small class="ml-2">mdi-content-copy</v-icon>
          </span>
        </div>
        <div class="font-weight-medium text-start py-6" v-if="tokenDescription">
          <v-col cols="16">
            {{ tokenDescription }}
          </v-col>
        </div>
        <div class="py-6">
          <v-btn
            large
            outlined
            color="primary"
            class="v-btn--wrap"
            :href="marketplaceTokenUri(contractAddr, tokenId)"
          >
            {{ $t('token.tradeInOpenSea') }}
          </v-btn>
        </div>
        <div class="py-6"><!-- v-if="tokenOwner == web3Account"> -->
          <v-btn v-if="!highResolutionUri"
            large
            outlined
            color="white"
            class="v-btn--wrap"
            @click="accessHRContent"
            :loading="isFetchingHRContent"
          >
            {{ $t('token.requestHRContent') }}
          </v-btn>
          <v-btn v-else
            large
            outlined
            color="primary"
            class="v-btn--wrap"
            :href="highResolutionUri"
            target="_blank"
          >
            {{ $t('token.openHRContent') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { contractAddr, totalTokens, explorerAddressUri, explorerTokenUri, marketplaceTokenUri, revealHRContentMsg, fetchHRContentUri, fetchNftMetadataUri } from '@/data/constants.js'
import { shortenHash, translateIpfsUri } from '@/filters'

export default {
  components: {

  },
  props: {
    token_id: {
      type: [Number, String],
      default: '0'
    }
  },
  data () {
    return {
      isLoading: true,
      tokenTitle: null,
      imageUri: null,
      tokenOwner: null,
      tokenDescription: null,
      highResolutionUri: null,
      isFetchingHRContent: false
    }
  },
  metaInfo () {
    return {
      title: 'The QBits - ' + this.tokenTitleText
    }
  },
  computed: {
    contractAddr: () => contractAddr,
    tokenId () {
      const tokenIdType = typeof this.token_id
      if (tokenIdType !== 'string' && tokenIdType !== 'number') {
        return null
      }
      if (isNaN(this.token_id) || isNaN(parseInt(this.token_id))) {
        return null
      }
      const tokenId = parseInt(this.token_id)
      if (tokenId < 0 || tokenId >= totalTokens) {
        return null
      }
      return tokenId
    },
    web3LastChangeTimestamp () {
      return this.$store.getters.web3LastChangeTimestamp
    },
    ethProvider () {
      return this.$store.getters.ethProvider
    },
    ethSigner () {
      return this.$store.getters.ethSigner
    },
    ethContractInstance () {
      return this.$store.getters.ethContractInstance
    },
    isWalletConnected () {
      return this.$store.getters.web3Connected &&
          !!this.$store.getters.web3Account
    },
    isChainMatched () {
      return this.$store.getters.web3ChainMatch
    },
    isWalletConnectedToChain () {
      return this.isWalletConnected && this.isChainMatched
    },
    web3Account () {
      return this.$store.getters.web3Account
    },
    isWeb3Connected () {
      return !!this.ethProvider
    },
    isWeb3ConnectedToChain () {
      return this.isWeb3Connected && this.isChainMatched
    },
    tokenTitleText () {
      return this.tokenTitle || this.$t('token.genericTokenTitle', [this.tokenId])
    },
    tokenOwnerDisplay () {
      return this.tokenOwnerName || shortenHash(this.tokenOwner)
    }
  },
  methods: {
    explorerAddressUri,
    explorerTokenUri,
    marketplaceTokenUri,
    async updateTokenInfoFromChain () {
      if (this.tokenId == null) { return }
      this.isLoading = true
      let tokenMetadataRawUri = null
      this.tokenOwner = null
      this.tokenOwnerName = null
      try {
        if (this.isWeb3ConnectedToChain) {
          tokenMetadataRawUri = await this.ethContractInstance.tokenURI(this.tokenId)
          this.tokenOwner = await this.ethContractInstance.ownerOf(this.tokenId)
          try {
            this.tokenOwnerName = await this.ethProvider.lookupAddress(this.tokenOwner)
          } catch {}
        } else {
          const tokenMetadataApiResponse = await fetch(fetchNftMetadataUri(contractAddr, this.tokenId), {
            method: 'GET',
            headers: {
              Accept: 'application/json'
            }
          })
          if (!tokenMetadataApiResponse.ok) { throw new Error() }
          const tokenMetadataApiBody = await tokenMetadataApiResponse.json()
          // REVISAR if (tokenMetadataApiBody.asset_contract.address.toLowerCase() !== contractAddr.toLowerCase()) { throw new Error() }
          if (String(tokenMetadataApiBody.token_id) !== String(this.tokenId)) { throw new Error() }
          this.tokenOwner = tokenMetadataApiBody.owner.address
          tokenMetadataRawUri = tokenMetadataApiBody.token_metadata
        }
      } catch (error) {
        console.error(error) // eslint-disable-line no-console
        this.$store.dispatch('alertShow', { error: this.$t('error.fetchingTokenData') })
      }
      const tokenMetadataUri = translateIpfsUri(tokenMetadataRawUri
        .replace(/\{id\}}/g, this.tokenId.toString())
      )
      try {
        const tokenMetadataResponse = await fetch(tokenMetadataUri, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        })
        if (!tokenMetadataResponse.ok) { throw new Error() }
        const tokenMetadata = await tokenMetadataResponse.json()
        this.tokenTitle = tokenMetadata.name
        this.imageUri = translateIpfsUri(
          tokenMetadata.image
            .replace(/\{id\}}/g, this.tokenId.toString())
        )
        this.tokenDescription = tokenMetadata.description
      } catch (error) {
        console.error(error) // eslint-disable-line no-console
        this.$store.dispatch('alertShow', { error: this.$t('error.fetchingTokenData') })
      }
      this.isLoading = false
    },
    async accessHRContent () {
      if (!this.isWalletConnectedToChain) { return }
      if (this.tokenId == null) { return }
      // if (this.tokenOwner !== this.web3Account) { return }
      this.isFetchingHRContent = true
      const challengeMsg = revealHRContentMsg(this.tokenId)
      let challengeSignature
      try {
        challengeSignature = await this.ethSigner.signMessage(challengeMsg)
      } catch {
        this.$store.dispatch('alertShow', { error: this.$t('error.challengeNotSigned') })
        this.isFetchingHRContent = false
        return
      }
      try {
        const fetchHRContentResponse = await fetch(fetchHRContentUri(this.tokenId, challengeSignature), {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'application/json'
          }
        })
        if (!fetchHRContentResponse.ok) { throw new Error() }
        const fetchHRContent = await fetchHRContentResponse.json()
        this.highResolutionUri = fetchHRContent.uri
        setTimeout(() => {
          this.highResolutionUri = null
        }, fetchHRContent.expiresIn * 1000)
      } catch {
        this.$store.dispatch('alertShow', { error: this.$t('error.couldntRetrieveHRContentURI') })
      }
      this.isFetchingHRContent = false
    }
  },
  watch: {
    isWeb3ConnectedToChain () {
      this.updateTokenInfoFromChain()
    },
    $route () {
      this.updateTokenInfoFromChain()
    }
  },
  mounted () {
    this.updateTokenInfoFromChain()
  }
}
</script>

<style lang="scss">
  #token-view {
    margin-bottom:400px;
  }

</style>
